import { useLayoutEffect, useRef, useState } from "preact/hooks";
import {
  listById,
  listLabel,
  required,
  styled,
  ToucanEmptyNote,
  ToucanFab,
  ToucanSearchField,
  useSearchDocked,
} from "toucan";
import { ItemCard } from "../components/ItemCard.tsx";
import { ListChips } from "../components/ListChips.tsx";
import { SortMenu } from "../components/SortMenu.tsx";
import {
  alongTheRows,
  columnsThatFit,
  dealIntoColumns,
  itemsInView,
  scopeOfSort,
  searchItems,
  sortItems,
  unfiledItems,
} from "../model.ts";
import { progressIn } from "../place.ts";
import { waitingFor } from "../precache.ts";
import { go, listPath } from "../routes.ts";
import { lists, store } from "../state.ts";
import type { RefObject } from "preact";
import type { HeldContent } from "../precache.ts";
import type { ListFilter } from "toucan";
import type { ArchiveScope, Item, ItemSort } from "../model.ts";

const Page = styled("section")`
  min-height: 100%;
  /* Warm canvas backdrop so the white cards read as raised surfaces; the
     header keeps its own white. */
  background: var(--toucan-canvas);

  .reads-toolbar {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.75rem 1rem 0.25rem;
  }

  /* The search box gives up whatever width the sort control needs, since a
     sort pill that shrinks stops naming the order. */
  .reads-controls {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .reads-controls .search {
    display: block;
    flex: 1 1 auto;
    min-width: 0;
  }

  /* One column on a phone, several side by side on a wide screen. Each card
     is only as tall as its content, since many have no picture. Separate
     columns rather than grid rows, because a grid row is as tall as its
     tallest card and leaves gaps under the others. dealIntoColumns in the
     model decides which card goes in which column. */
  .reads-grid {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    padding: 0.75rem 1rem 5rem;
  }

  .reads-column {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    /* An equal share of the row each; sized by content, the column holding
       the longest title would be the widest. */
    flex: 1 1 0;
    min-width: 0;
  }

  @media (min-width: 720px) {
    .reads-grid {
      gap: 1rem;
      padding: 1rem 1.5rem 5rem;
    }

    .reads-column {
      gap: 1rem;
    }
  }

  .reads-grid .panel-empty {
    flex: 1;
    text-align: center;
  }

  /* Main scrolls, so the Add button pins to the viewport rather than to the
     page it would scroll away with. */
  & > .fab {
    position: fixed;
    right: 16px;
    bottom: 16px;
  }
`;

/**
 * The list an item's card names, even in a library narrowed to that list, so
 * the card doesn't change with the filter.
 */
function cardList(item: Item): string | undefined {
  if (item.listId === undefined) {
    return undefined;
  }
  const list = listById(store().lists, item.listId);
  // A list deleted on another device can leave an id behind it. The card says
  // nothing rather than dropping the item out of the library.
  return list === undefined ? undefined : listLabel(list);
}

/**
 * How many columns of cards the box `grid` names is wide enough for, kept up
 * to date as it changes size.
 *
 * The box is measured rather than the viewport, so the page's padding is
 * already taken off. Measured before the first paint too, since the observer
 * answers after it, and the library would draw as one column and then
 * rearrange every card on each return from an article.
 */
function useColumns(grid: RefObject<HTMLDivElement | null>): number {
  const [columns, setColumns] = useState(1);
  useLayoutEffect(() => {
    const box = required(grid.current, "the card grid");
    setColumns(columnsThatFit(box.clientWidth));
    const watcher = new ResizeObserver((entries) => {
      setColumns(
        columnsThatFit(required(entries[0], "resized box").contentRect.width),
      );
    });
    watcher.observe(box);
    return () => {
      watcher.disconnect();
    };
  }, [grid]);
  return columns;
}

/** The empty library's message, in the words of what was asked for. */
function nothing(searching: boolean, archive: ArchiveScope): string {
  if (searching) {
    return "Nothing matches.";
  }
  if (archive === "archived") {
    return "Nothing archived yet.";
  }
  return store().items.length > 0
    ? "Nothing here."
    : "Nothing saved yet — tap ＋ to keep a link for later.";
}

/**
 * The library, with the search field and the list chips across the top and
 * the cards below them, and a button that saves another link.
 *
 * `query` is held by the shell, so it survives reading a result. See `Shell`.
 */
export function LibraryPage({
  listId,
  sort,
  query,
  held,
  onSort,
  onQuery,
}: {
  listId: ListFilter;
  sort: ItemSort;
  query: string;
  /** What of the library is on this device, as the precache found it. */
  held: HeldContent;
  onSort: (sort: ItemSort) => void;
  onQuery: (query: string) => void;
}) {
  const grid = useRef<HTMLDivElement>(null);
  const columns = useColumns(grid);
  // Search and sort share the top line while the chips fit, and the sort
  // moves down beside their pill once they fold. With the search docked, the
  // sort has the chips' line throughout.
  const docked = useSearchDocked();
  const [folded, setFolded] = useState(false);
  const searching = query.trim() !== "";
  // A search from the unarchived items reaches the archive too, since that is
  // where an old item you can't find will be. A search from the archive stays
  // in the archive.
  const scope = scopeOfSort(sort);
  let archive: ArchiveScope = scope;
  if (searching) {
    archive = scope === "archived" ? "archived" : "everything";
  }
  const shown = sortItems(
    searchItems(itemsInView(store(), { listId, archive }), query),
    sort,
  );
  return (
    <Page>
      <div class="reads-toolbar">
        <div class="reads-controls">
          <ToucanSearchField
            class="search"
            docked
            label="Search saved links"
            placeholder="Search articles…"
            screen="search"
            query={query}
            onQuery={onQuery}
          />
          {!docked && !folded && <SortMenu sort={sort} onSort={onSort} />}
        </div>
        <ListChips
          lists={lists()}
          unfiled={unfiledItems(store()).length > 0}
          pressed={listId}
          onPick={(picked) => {
            go(listPath(picked));
          }}
          aside={<SortMenu sort={sort} onSort={onSort} />}
          asideOnceFolded={!docked}
          onFold={setFolded}
        />
      </div>
      <div
        class="reads-grid"
        ref={grid}
        onKeyDown={(event: KeyboardEvent) => {
          if (event.key !== "Tab") return;
          const ring = alongTheRows([
            ...required(grid.current, "grid").querySelectorAll(".reads-column"),
          ]);
          const at = ring.indexOf(document.activeElement as HTMLElement);
          // Off either end, the browser's own Tab leaves the library.
          const next =
            at === -1 ? undefined : ring[at + (event.shiftKey ? -1 : 1)];
          if (next === undefined) return;
          event.preventDefault();
          next.focus();
        }}
      >
        {shown.length === 0 ? (
          <ToucanEmptyNote>{nothing(searching, archive)}</ToucanEmptyNote>
        ) : (
          dealIntoColumns(shown, columns).map((column, index) => (
            // A column has no identity beyond where it is in the row.
            <div class="reads-column" key={index}>
              {column.map((item) => (
                <ItemCard
                  key={item.id}
                  item={item}
                  list={cardList(item)}
                  progress={progressIn(item.id)}
                  waiting={waitingFor(item, held)}
                  onOpen={() => {
                    go(`read/${item.id}`);
                  }}
                  onEdit={() => {
                    go(`edit/${item.id}`);
                  }}
                />
              ))}
            </div>
          ))
        )}
      </div>
      <ToucanFab
        class="fab"
        bar
        label="Save a link"
        onClick={() => {
          go("add");
        }}
      />
    </Page>
  );
}
